/**
 * Shared Drawer component - slides in from the right
 */
import React from "react";

type DrawerProps = {
  open: boolean;
  onClose: () => void;
  width?: string;
  children: React.ReactNode;
};

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.35)",
  zIndex: 9998,
};

const drawerStyle = (width: string): React.CSSProperties => ({
  position: "fixed",
  right: 0,
  top: 0,
  height: "100vh",
  width,
  background: "var(--color-surface)",
  zIndex: 9999,
  borderLeft: "1px solid var(--color-border)",
  boxShadow: "0 10px 30px rgba(0,0,0,.2)",
  padding: 14,
  display: "grid",
  gridTemplateRows: "auto 1fr auto",
  gap: 12,
  overflowY: "auto",
});

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  width = "min(740px, 92vw)",
  children,
}) => {
  if (!open) return null;

  return (
    <>
      <div style={backdropStyle} onClick={onClose} />
      <div style={drawerStyle(width)} role="dialog">
        {children}
      </div>
    </>
  );
};

// Header component for drawer
type DrawerHeaderProps = {
  title: string;
  onClose: () => void;
  actions?: React.ReactNode;
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
};

const titleStyle: React.CSSProperties = {
  fontWeight: 800,
  fontSize: 16,
  color: "var(--color-title)",
};

const closeButtonStyle: React.CSSProperties = {
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  borderRadius: 10,
  width: 32,
  height: 32,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
};

export const DrawerHeader: React.FC<DrawerHeaderProps> = ({
  title,
  onClose,
  actions,
}) => {
  return (
    <div style={headerStyle}>
      <span style={titleStyle}>{title}</span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {actions}
        <button
          type="button"
          onClick={onClose}
          style={closeButtonStyle}
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// Body component for drawer
export const DrawerBody: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div style={{ overflow: "auto", display: "grid", gap: 12 }}>{children}</div>
  );
};

// Footer component for drawer
export const DrawerFooter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
      {children}
    </div>
  );
};

export default Drawer;
